<script setup lang="ts">
import { ref } from 'vue';
import BaseDialog from '@/components/base/BaseDialog.vue';
import { getLabel } from '@/utils/data-display-utils';
import BaseButton from '@/components/base/BaseButton.vue';
import { authService } from '@/services/auth.service';
import { constant } from '@/constant';
import { HomepageItem } from '@/store/homepageItem.interface';
import { Locales } from '@/store/language.interface';

// PROPS
const props = defineProps<{
  homeLink: boolean;
  needLogin: boolean;
  item: HomepageItem | null;
  languageEnabled?: Locales[];
}>();

// REFS
const homepageItemPrivateNoAccessDialogRef = ref<InstanceType<typeof BaseDialog>>();

// FUNCTIONS
function openDialog() {
  homepageItemPrivateNoAccessDialogRef.value?.openDialog();
}

function closeDialog() {
  homepageItemPrivateNoAccessDialogRef.value?.closeDialog();
}

defineExpose({
  openDialog,
  closeDialog,
});

function goBackHomeClick() {
  window.location.href = '/';
}

function loginClick() {
  if (props.item.itemType === constant.HomePageItem.Type.Consultation) {
    authService.login({ consultation: { slug: props.item.slug } });
  } else if (props.item.itemType === constant.HomePageItem.Type.Project) {
    authService.login({ project: { slug: props.item.slug } });
  }
}

function getTranslationByItemType(key: string): string {
  const value = (() => {
    switch (props.item.itemType) {
      case constant.HomePageItem.Type.Project:
        return 'project';
      case constant.HomePageItem.Type.Consultation:
      default:
        return 'consultation';
    }
  })();
  return getLabel(`dialog.homepageItemPrivateNoAccessDialog.${value}.${key}`, props.languageEnabled);
}
</script>

<template>
  <BaseDialog ref="homepageItemPrivateNoAccessDialogRef">
    <template v-slot:title>
      <div class="homepageItemPrivateNoAccess-title">
        {{ getTranslationByItemType('title') }}
      </div>
    </template>
    <template v-slot:content>
      <div class="homepageItemPrivateNoAccess-content">
        <p v-if="needLogin">
          {{ getTranslationByItemType('contentNeedLogin') }}
        </p>
        <p v-else>
          {{ getTranslationByItemType('content') }}
        </p>
        <div class="homepageItemPrivateNoAccess-actions">
          <BaseButton v-if="homeLink" class="action" @click="goBackHomeClick"
            >{{ getLabel('dialog.homepageItemPrivateNoAccessDialog.home', props.languageEnabled) }}
          </BaseButton>
          <BaseButton v-if="needLogin" class="action" @click="loginClick"
            >{{ getLabel('dialog.homepageItemPrivateNoAccessDialog.login', props.languageEnabled) }}
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseDialog>
</template>

<style lang="scss">
.homepageItemPrivateNoAccess {
  &-title {
    font-size: $medium-title;
  }

  &-actions {
    display: flex;
    justify-content: end;
    margin-top: $space-md;
  }
}

.base-dialog {
  background-color: $color-neutral-background !important;
}
</style>
